import { LocalizationProvider } from "@mui/x-date-pickers";
import { Frame } from "../common/Frame";
import { Button, Grid, Checkbox, CircularProgress } from "@mui/material";
import { DateTime, DateTimeRange } from "../common/DateRange";
import { DATE_FORMAT, getDateFnsLocale } from "../date-utils";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSearchParams } from "react-router-dom";
import { addDays, formatISO, isValid, parse, startOfDay, subMonths } from "date-fns";
import { ChangeEvent, useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { format } from 'date-fns';
import useAxios from "axios-hooks";
import Para from 'papaparse'
import ErrorHandler from "../ErrorHandler";
import JSZip from "jszip";

const locale = getDateFnsLocale();

export function CsvDownload() {

  const [searchParam, setSearchParams] = useSearchParams();

  const paramFrom = parse(searchParam.get('from') || '', DATE_FORMAT, new Date());
  const paramTo = parse(searchParam.get('to') || '', DATE_FORMAT, new Date());

  const today = startOfDay(new Date());
  const initFrom = isValid(paramFrom) ? paramFrom : subMonths(addDays(today, 1), 1);
  const initTo = isValid(paramTo) ? paramTo : today;

  const [from, setFrom] = useState<DateTime | null>(new DateTime(initFrom, null));
  const [to, setTo] = useState<DateTime | null>(new DateTime(initTo, null, true));

  const { user } = useAuthenticator();
  const token = user?.getSignInUserSession()?.getIdToken().getJwtToken();

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formatFrom = from && isValid(from.dateTime()) && formatISO(from.dateTime());
  const formatTo = to && isValid(to.dateTime()) && formatISO(to.dateTime());
  const [open, setOpen] = useState(false);
  const onClose = () => setOpen(false);

  const [loading, setLoading] = useState(false);

  const [{error}, download] = useAxios<Blob>({
    url: `${process.env.REACT_APP_AWS_API_GATEWAY_URL}/material/csv`,
    headers: { Authorization: token },
    responseType: 'blob',
    params: { from: formatFrom, to: formatTo, timezone }
  },
    { manual: true }
  );

  const getCurrentDateTime = () => {
    return format(new Date(), "yyyyMMdd'T'HHmmss");
  };

  /**
   * furuo_edit
   * 選択したcsvファイルのみダウンロード
   */

  interface Csv {
    label: string;
    checked: boolean;
  }
  
  //初期状態
  const [csvs, setCsvs] = useState<Csv[]>([
      { label: "material.csv", checked: true },
      { label: "scene.csv", checked: true },
      { label: "area.csv", checked: true },
    ]);
  
  //チェック状態の管理,初期状態は全てtrue
  const [checkList, setCheckList] = useState<Csv[]>([
      { label: "material.csv", checked: true },
      { label: "scene.csv", checked: true },
      { label: "area.csv", checked: true },
  ]);
  //チェック状態更新のハンドラ
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newItem = [...csvs];
    newItem.map((item) => {
      if (item.label === e.target.value) {
        item.checked = !item.checked;
      }
      return item;
    });

    setCsvs(newItem);

    const checkItem = newItem.filter((item) => item.checked);
    setCheckList(checkItem);
  };

  const downloadCsv = (filename: string, csv: string) => {
    const url = window.URL.createObjectURL(new Blob([BOM, csv], { type: 'text/csv; charset=utf-8-bom' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
  }

  const onDownloadClicked = () => {
    if (!formatFrom || !formatTo || checkList.every(item => !item.checked)) {
      return;
    }
    setLoading(true);

    const zip = new JSZip();
    let csvContent = "";
    download().then(res => {
      res.data.text().then(text => {
        const { data } = Para.parse<any>(text, { header: true, skipEmptyLines: true });

        checkList.forEach(item => {
          if (item.label === 'material.csv') {
            const materialData = data
              .filter((d, i) => i === 0 || d["ID"] !== data[i - 1]["ID"])
              .map(d => MATERIAL_COLUMNS.map(i => d[i]));
            csvContent = Para.unparse([MATERIAL_COLUMNS, ...materialData], { header: true });
          } else if (item.label === 'scene.csv') {
            const sceneData = data
              .filter((d, i) => i === 0 || d["Scene ID"] !== data[i - 1]["Scene ID"])
              .map(d => SCENE_COLUMNS.map(i => d[i]));
            csvContent = Para.unparse([SCENE_COLUMNS, ...sceneData], { header: true });
          } else if (item.label === 'area.csv') {
            const areaData = data.map(d => AREA_COLUMNS.map(i => d[i]));
            csvContent = Para.unparse([AREA_COLUMNS, ...areaData], { header: true });
          }
          if (csvContent) {
            zip.file(item.label, new Blob([BOM, csvContent], { type: 'text/csv; charset=utf-8-bom' }));
          }
        });
  
        if (checkList.length > 1) {
          zip.generateAsync({ type: "blob" }).then(function(content) {
            const formattedDateTime = getCurrentDateTime();
            const filename = `vrl-dashboard_${formattedDateTime}.zip`;
            const url = window.URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            setLoading(false);
          });
        } else if (checkList.length === 1) {
          const formattedDateTime = getCurrentDateTime();
          const filename = `${checkList[0].label.split('.').slice(0, -1).join('.')}_${formattedDateTime}.${checkList[0].label.split('.').pop()}`;
          downloadCsv(filename, csvContent);
          setLoading(false);
        }
      });
    }).catch(() => {
      setLoading(false);
    });
  }

  const errorMessage = (error && (error.response?.data?.message || error.message)) || '';
  const errorCode = (error && (error.response?.data?.code || error.code)) || '';
  
  useEffect(() => {
    if (error) {
      setOpen(true);
    }
  }, [error]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <Frame title="CSV File Download">
        <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
          <DateTimeRange from={from} setFrom={setFrom} to={to} setTo={setTo} />
          
          <Grid container alignItems="center" justifyContent="center">
            <div>
              {csvs.map((csv) => (
                <label key={csv.label}>
                  <Checkbox
                    value={csv.label}
                    checked={csv.checked}
                    onChange={handleChange}
                  />
                  {csv.label}
                </label>
              ))}
              
            </div>
          </Grid>

          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <Button onClick={onDownloadClicked} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Download'}
            </Button>
          </Grid>
        </Grid>
      </Frame>
      <ErrorHandler open={open} onClose={onClose} errorMessage={errorMessage} errorCode={errorCode} />
    </LocalizationProvider>
  )
}

const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);

const MATERIAL_COLUMNS = [
  'Start Time', 'Device ID', 'Device Name', 'Version', 'User Name', 'Name', 'Material ID', 'Material Name',
];

const SCENE_COLUMNS = [
  'Start Time', 'Device ID', 'Device Name', 'Version', 'User Name', 'Name', 'Material ID', 'Material Name',
  'Scene Display Order',
  'Scene Name', 'Scene Start Time', 'Scene End Time', 'Scene End Type',
  'Clicked Area Count', 'Total Area Count', 'Correct Question Count', 'Total Question Count',
]

const AREA_COLUMNS = [
  'Start Time', 'Device ID', 'Device Name', 'Version', 'User Name', 'Name', 'Material ID', 'Material Name',
  'Scene Display Order',
  'Scene Name', 'Scene Start Time', 'Scene End Time', 'Scene End Type',
  'Clicked Area Count', 'Total Area Count', 'Correct Question Count', 'Total Question Count',
  'Area Display Order',
  'Area Name', 'Score Subject', 'Area Result', 'Answer Second', 'Area Options',
];
